import { useCallback } from 'react';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';

import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';
import useStatementFileUpload from 'modules/common/components/StatementUpload/hooks/useStatementFileUpload';
import STATEMENT_UPLOAD_ANALYTICS_EVENTS from 'services/statementUploadTasks/constants/analytics';
import useDeleteMemberDocument from 'services/documents/mutations/useDeleteMemberDocument';
import UploadedFileRow from 'modules/common/components/UploadedFileRow';
import BankLogo from 'modules/HubV2/Bookkeeping/components/Banks2/BankList/Bank/BankLogo';
import bankAccountLabels from 'services/hurdlr/constants/bankAccountLabels';
import { BankAccountSubType } from 'services/hurdlr/queries/useHurdlrPlaidItems/useHurdlrPlaidItems.interface';
import { Document } from 'modules/common/interfaces/documents';

interface AccountUploadRowProps {
    readonly accountMask: string;
    readonly accountName: string;
    readonly accountSubType: BankAccountSubType;
    readonly plaidInstitutionId: string;
    readonly year: number;
    readonly month: number;
    readonly documents: Document[];
    readonly taskId: string;
    readonly institutionName: string;
    readonly showDate?: boolean;
}

export default function AccountUploadRow({
    accountMask,
    accountName,
    accountSubType,
    plaidInstitutionId,
    year,
    month,
    documents,
    taskId,
    institutionName,
    showDate = false,
}: Readonly<AccountUploadRowProps>) {
    const { handleUpload, isUploading } = useStatementFileUpload({
        accountMask,
        plaidInstitutionId,
        year,
        month,
        taskId,
        institutionName,
    });

    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { sendTrackingEvent } = useEventTracking();
    const deleteMemberDocument = useDeleteMemberDocument();

    const handleDelete = useCallback(
        (documentUuid: string) =>
            new Promise<void>((resolve) => {
                deleteMemberDocument.mutate(documentUuid, {
                    onSuccess: async () => {
                        sendTrackingEvent(STATEMENT_UPLOAD_ANALYTICS_EVENTS.DOCUMENT_DELETED, {
                            task_id: taskId,
                            institution_name: institutionName,
                            accountMask,
                            plaidInstitutionId,
                            year,
                            month,
                            documentUuid,
                        });
                        await queryClient.invalidateQueries({ queryKey: ['documents'] });
                        enqueueSnackbar('Document deleted successfully', { variant: 'success' });
                        resolve();
                    },
                    onError: () => {
                        enqueueSnackbar('Error deleting file', { variant: 'error' });
                        resolve();
                    },
                });
            }),
        [
            deleteMemberDocument,
            enqueueSnackbar,
            queryClient,
            sendTrackingEvent,
            taskId,
            institutionName,
            accountMask,
            plaidInstitutionId,
            year,
            month,
        ]
    );

    const hasFiles = documents.length > 0;

    const accountTypeLabel = bankAccountLabels[accountSubType] ?? accountSubType;

    return (
        <Stack spacing={1} sx={{ py: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={2}>
                    <BankLogo institutionId={plaidInstitutionId} width={40} height={40} />
                    <Stack>
                        <Typography variant="body1" fontWeight="bold">
                            {accountName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {accountTypeLabel} &ensp;{'\u2022'}&ensp; {accountMask}
                        </Typography>
                        {showDate && (
                            <Typography variant="body2" color="primary" fontWeight={600}>
                                {dayjs()
                                    .month(month - 1)
                                    .format('MMMM')}{' '}
                                {year}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
                <Button
                    variant="outlined"
                    color="neutral"
                    onClick={handleUpload}
                    disabled={isUploading}
                    sx={{ minWidth: 100 }}>
                    {isUploading ? <CircularProgress size={20} color="inherit" /> : 'Upload'}
                </Button>
            </Stack>
            {hasFiles && (
                <Stack>
                    {documents.map((doc, index) => (
                        <UploadedFileRow
                            key={doc.uuid}
                            name={doc.file_name}
                            isFirst={index === 0}
                            isLast={index === documents.length - 1}
                            onDelete={() => handleDelete(doc.uuid)}
                        />
                    ))}
                </Stack>
            )}
        </Stack>
    );
}
